/**
 * A way of playing said once over the staff, and where it holds.
 *
 * A player is told how to sound the notes by a word -- `pizz.`, `con sord.`, `sul pont.` --
 * and goes on that way until another word ends it: over bar lines and through any number of
 * rests. So how a note is played is written neither on the note nor in its bar. It is
 * whatever the last such word before it said.
 *
 * The words are read in the order the bars are written and not in the order they are
 * played, which is how the player reads them: a repeat goes back to bars that stand before
 * the word and are played the second time as they were the first.
 */

import type { EventId } from "../document/ids";
import type { Part, PartMeasure, Score } from "../document/types";
import { onsets } from "./graces";

/** What a word says of one way of playing: begun, ended, or nothing at all. */
export type Said = (text: string) => boolean | null;

/** A place in a bar from which the part plays that way, or stops. */
interface Turn {
  readonly start: number;
  readonly on: boolean;
}

/** The turns one bar makes, in the order they are reached. */
function turnsOf(measure: PartMeasure, said: Said): readonly Turn[] {
  const words = measure.directions.flatMap((direction) => {
    if (direction.content.kind !== "words") return [];
    const on = said(direction.content.text);
    return on === null ? [] : [{ anchor: direction.anchor, on }];
  });
  if (words.length === 0) return [];

  // A word stands on an event and takes effect where that event begins, in whichever line
  // of the bar it is kept: the whole part is told, not one voice of it.
  const starts = new Map<EventId, number>();
  for (const voice of measure.voices) {
    if (voice.ossia) continue;
    for (const { event, start } of onsets(voice)) starts.set(event.id, start);
  }
  return words
    .flatMap(({ anchor, on }) => {
      const start = starts.get(anchor);
      return start === undefined ? [] : [{ start, on }];
    })
    .sort((left, right) => left.start - right.start);
}

export interface Holding {
  /** Whether a note beginning `start` ticks into the bar at `index` is played that way. */
  at(index: number, start: number): boolean;
}

/** Where `part` plays the way `said` reads the words for, bar by bar of the written order. */
export function holdingOf(score: Score, part: Part, said: Said): Holding {
  const entering: boolean[] = [];
  const turns = new Map<number, readonly Turn[]>();
  let on = false;
  score.measures.forEach((global, index) => {
    entering.push(on);
    const measure = part.measures[global.id];
    if (measure === undefined) return;
    const made = turnsOf(measure, said);
    if (made.length === 0) return;
    turns.set(index, made);
    on = made[made.length - 1]?.on ?? on;
  });

  return {
    at(index, start) {
      let state = entering[index] ?? false;
      for (const turn of turns.get(index) ?? []) {
        if (turn.start > start) break;
        state = turn.on;
      }
      return state;
    },
  };
}
